<template>
    <div class="trip-destinations-map">

        <!-- Legend -->
        <div class="legend">
            <div class="legend-item" v-for="item in legend">
                <destination-map-marker
                :icon="item.icon"
                :fill-color="item.fillColor"
                :stroke-color="item.strokeColor"></destination-map-marker>

                <span class="name" v-if="item.type">
                    {{ 'components.tripDestinationsMap.legend.' + item.type | trans }}
                </span>

                <span class="name" v-if="item.label">
                    {{ item.label }}
                </span>
            </div>
        </div>

        <!-- Map -->
        <GmapMap ref="mapRef" :center="center" style="height: 400px;" :options="options" :zoom="12">
            <gmap-info-window
            :options="infoOptions"
                :position="infoPosition"
                :opened="infoOpened"
                @closeclick="infoOpened=false">
                    <div v-html="infoContent"></div>
                </gmap-info-window>

            <gmap-custom-marker
                :marker="marker"
                :key="index"
                v-for="(marker, index) in markers"
                @click.native="() => { center = marker; selectedDestination = marker.destination; toggleInfoWindow(marker, index) }"
                >
                <destination-map-marker
                    :icon="marker.icon"
                    :label="marker.label"
                    :fill-color="marker.fillColor"
                    :stroke-color="marker.strokeColor"></destination-map-marker>
            </gmap-custom-marker>
        </GmapMap>

        <div class="horizontal-scrollflow-container" v-if="destinations.length > 1">
            <!-- Arrow right -->
            <span class="arrow-right" @click.prevent="scrollRight">
                <i class="wa-icon-long-arrow-right"></i>
            </span>

            <div class="horizontal-scrollflow destinations mt-3">
                <!-- Destination cards -->
                <div class="destination-card" v-for="destination in destinations" style="max-width: 140px;" @mouseover="selectDestination(destination)" :class="{ active: selectedDestination && selectedDestination.id === destination.id }">
                    <!-- Image -->
                    <img :src="destination.image + '?width=140&height=100'" :srcset="destination.image + '?width=140&height=100 1x, ' + destination.image + '?width=280&height=200 2x'" v-if="destination.image" class="img-fluid" />

                    <!-- Name -->
                    <span class="name">
                        {{ destination.name }}

                        <!-- Stars -->
                        <span class="stars" v-if="destination.stars">
                            <i class="fa fa-star" v-for="starIndex in parseInt(destination.stars, 10)"></i>
                            <i class="fa fa-star-half" v-if="destination.stars % 1 > 0"></i>
                        </span>
                    </span>

                    <!-- Description -->
                    <span class="description" v-if="destination.description">
                        {{ destination.description }}
                    </span>

                    <!-- Selection info -->
                    <span class="selection-info" v-if="destination.selection_info">
                        {{ destination.selection_info }}
                    </span>

                    <!-- Rating -->
                    <span class="rating" v-if="destination.rating">
                        <i class="fa fa-star" v-for="starIndex in (destination.rating / 1)"></i>
                        <i class="fa fa-star-half" v-if="destination.rating % 1 > 0"></i>
                        {{ destination.rating | formatNumber }}
                    </span>

                    <a href="#destination-details" class="btn btn-link btn-sm px-2 ml-n2" @click.prevent="showDestination(destination)">
                        {{ 'components.tripDestinationsMap.moreInfos' | trans }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import mapStyles from './destinatiosnMapStyles.json'
    import GmapCustomMarker from 'vue2-gmap-custom-marker';
    import DestinationMapMaker from './DestinationMapMarker.vue'

    export default {
        props: ['tripId', 'cdn', 'markerLabel', 'markerIcon', 'url', 'hotelDetailsEndpoint'],
        components: {
            'gmap-custom-marker': GmapCustomMarker,
            'destination-map-marker': DestinationMapMaker,
        },
        data() {
            return {
                destinations: [],
                activities: [],
                selectedDestination: null,
                type: 'destinations',

                center: { lat: 48.160910, lng: 16.383330 },

                // Info window
                currentMarkerIndex: null,
                infoPosition: null,
                infoContent: null,
                infoOpened: false,
                infoOptions: {
                    pixelOffset: {
                        width: 0,
                        height: -35
                    }
                },
            }
        },
        mounted() {
            this.load()
        },
        computed: {
            legend() {
                let symbols = []

                // Destinations
                if (this.type === 'destinations') {
                    // Destination
                    if (this.selectedDestination && this.selectedDestination.longitude && this.selectedDestination.latitude) {
                        symbols.push({
                            fillColor: '#212424',
                            strokeColor: '#212424',
                            icon: this.selectedDestination.icon,
                            type: 'destination',
                        })

                        // Hotel
                        if (this.selectedDestination.hotels) {
                            symbols.push({
                                fillColor: '#1ABCB2',
                                strokeColor: '#129E95',
                                icon: 'fa fa-home-alt',
                                type: 'hotel',
                            })
                        }
                    }
                }

                // Activities
                if (this.type === 'activities') {
                    if (this.activities.length > 0) {
                        // Activity
                        symbols.push({
                            fillColor: '#212424',
                            strokeColor: '#212424',
                            icon: this.markerIcon,
                            label: this.markerLabel,
                        })
                    }

                    // Hotel
                    if (this.destinations.length > 0) {
                        symbols.push({
                            fillColor: '#1ABCB2',
                            strokeColor: '#129E95',
                            icon: 'fa fa-home-alt',
                            type: 'hotel',
                        })
                    }
                }

                return symbols
            },
            markers() {
                var markers = []

                // Destinations
                if (this.type === 'destinations') {
                    // Destination + hotels
                    if (this.selectedDestination && this.selectedDestination.longitude && this.selectedDestination.latitude) {
                        // Push destination also as center
                        this.center = {
                            lat: this.selectedDestination.latitude,
                            lng: this.selectedDestination.longitude,
                        }
                        markers.push({
                            destination: this.selectedDestination,
                            lat: this.selectedDestination.latitude,
                            lng: this.selectedDestination.longitude,
                            fillColor: '#212424',
                            strokeColor: '#212424',
                            icon: this.selectedDestination.icon,
                        })

                        // Push hotels
                        if (this.selectedDestination.hotels) {
                            markers = markers.concat(this.selectedDestination.hotels.map((hotel) => {
                                // Calculate distance to center
                                let distance = this.distance(this.center.lat, this.center.lng, hotel.location.latitude, hotel.location.longitude);

                                return {
                                    type: 'primary',
                                    hotel: hotel,
                                    lat: hotel.location.latitude,
                                    lng: hotel.location.longitude,
                                    icon: 'fa fa-home-alt',
                                    label: distance.toFixed(1).replace('.', ',') + ' km',
                                    fillColor: '#1ABCB2',
                                    strokeColor: '#129E95',
                                }
                            }))
                        }
                    }
                }

                // Activities
                if (this.type === 'activities') {
                    if (this.selectedDestination && this.selectedDestination.longitude && this.selectedDestination.latitude) {
                        // Push activities
                        for (var i = 0; i < this.activities.length; i++) {
                            markers.push({
                                lat: this.activities[i].latitude,
                                lng: this.activities[i].longitude,
                                fillColor: '#212424',
                                strokeColor: '#212424',
                                icon: this.markerIcon,
                            })
                        }

                        // Push hotel also as center
                        this.center = {
                            lat: this.selectedDestination.latitude,
                            lng: this.selectedDestination.longitude,
                        }
                        // Calculate distance to center
                        let distance = null
                        if (this.activities.length > 0) {
                            let distance = this.distance(this.activities[0].latitude, this.activities[0].longitude, this.selectedDestination.latitude, this.selectedDestination.longitude);
                        }

                        markers.push({
                            destination: this.selectedDestination,
                            lat: this.selectedDestination.latitude,
                            lng: this.selectedDestination.longitude,
                            icon: 'fa fa-home-alt',
                            label: distance ? distance.toFixed(1).replace('.', ',') + ' km' : null,
                            fillColor: '#1ABCB2',
                            strokeColor: '#129E95',
                        })
                    }
                }

                // if (markers.length > 0)
                    // this.center = markers[0]

                return markers
            },

            options() {
                return {
                    styles: mapStyles,
                    disableDefaultUI: true,
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: true,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                }
            }
        },
        methods: {
            load() {
                var self = this;

                // URL
                let url = this.url ? this.url : route('api.frontend.maps.destinations', {
                    trip: this.tripId,
                })

                axios.get(url, {}).then(function (response) {
                    self.type = response.data.meta.type

                    // Destinations
                    if (self.type === 'destinations') {
                        self.destinations = response.data.data

                    // Activities
                    } else if (self.type === 'activities') {
                        self.destinations = response.data.data.hotels
                        self.activities = response.data.data.activities
                    }

                    // Set first selected destination
                    if (self.destinations.length > 0)
                        self.selectedDestination = self.destinations[0]
                }).catch(function (error) {
                    console.log(error);
                    self.$toast.error('Error. Please check the console.');
                });
            },

            selectDestination(destination) {
                if (! this.selectedDestination || (this.selectedDestination && this.selectedDestination.id !== destination.id)) {
                    this.selectedDestination = destination

                    this.center = {
                        lat: destination.hotels[0].location.latitude,
                        lng: destination.hotels[0].location.longitude,
                    }
                }
            },

            toggleInfoWindow(marker, index) {
                this.infoPosition = {
                    lat: marker.lat,
                    lng: marker.lng,
                };
                this.infoContent = `
                    <img class="d-block rounded mb-2" style="height: 100px;" src="${defaultCdn + 'hotels/' + marker.hotel.guid + '/' + marker.hotel.image + '?width=400'}" />
                    <h6 style="max-width: 150px;">${marker.hotel.name}</h6>
                    <p style="max-width: 150px;">${marker.hotel.address}</p>
                    `;

                if (this.currentMarkerIndex == index) {
                    this.infoOpened = ! this.infoOpened;
                } else {
                    this.infoOpened = true;
                    this.currentMarkerIndex = index;
                }
            },

            showDestination(destination) {
                if (this.$root.$refs.tripDestinationModal) {
                    this.$root.$refs.tripDestinationModal.setDestination(destination)
                }
            },

            distance(lat1, lon1, lat2, lon2, unit) {
                if ((lat1 == lat2) && (lon1 == lon2)) {
                    return 0;
                } else {
                    var radlat1 = Math.PI * lat1/180;
                    var radlat2 = Math.PI * lat2/180;
                    var theta = lon1-lon2;
                    var radtheta = Math.PI * theta/180;
                    var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
                    if (dist > 1) {
                        dist = 1;
                    }
                    dist = Math.acos(dist);
                    dist = dist * 180/Math.PI;
                    dist = dist * 60 * 1.1515;
                    dist = dist * 1.609344
                    return dist;
                }
            },
            scrollRight() {
                var scrollContainer = $('div.horizontal-scrollflow-container div.horizontal-scrollflow');

                scrollContainer.animate({
                    scrollLeft: scrollContainer.scrollLeft() + 340
                }, 300);
            },
        },
        watch: {
            center() {
                var self = this
                this.$refs.mapRef.$mapPromise.then((map) => {
                    if (self.markers.length > 1) {
                        var bounds = new self.$window.google.maps.LatLngBounds();
                        for (let i = 0; i < self.markers.length; i++) {
                            bounds.extend(self.markers[i]);
                        }
                        map.fitBounds(bounds)
                        map.panToBounds(bounds)

                        var zoom = map.getZoom();
                        map.setZoom(zoom > 12 ? 12 : zoom);
                    } else if (self.markers.length <= 1 && self.center) {
                        map.panTo(self.center)
                    }

                    // console.log(map)
              })
            },
        }
    }
</script>
